import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { Column, ColumnKeys, InlineActionStatus, ResponseGridWebComponentSchema, Status } from '../types';
import { SectionType, SmartViewSection } from '../../../grid/gridSmartViews/types';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import { Filter } from '../../../grid/gridFilters/types';
import { ResponseGridDataProvider } from '../dataProvider/ResponseGridDataProvider';
import { GridSmartViewsWebComponent } from '../../../grid/gridSmartViews';
import { ResponseConstants } from '../ResponseConstants';
import { dateFormatter } from '@nirvana/shared/time';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { getPropertyByPath, replaceParams } from '../../../utils/objectOperations.utils';
import { html } from 'lit';
import { getParams } from '../../../utils/xdrOverview/xAdrTextGenerator.utils';
import { GridTitleConfig } from '../../../grid/uiElements/gridViewTitle/types';
import { convertViewConfigToSmartConfig } from '../../../grid/utils/smartViews';
import { getChangeStatusFailureMessage } from '../utils';
import { Rights } from '@nirvana/shared/constants';

export class ResponseGridView extends BaseWebComponentView {
  public gridDataProvider: ResponseGridDataProvider;
  public viewSettings: ResponseGridWebComponentSchema;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');

    this.dom.$layout = this.shadow.querySelector('gz-push-body-layout');

    this.dom.$smartViewsTitle = this.shadow.querySelector('#smart-views-title');

    this.dom.$sideBarContent = this.shadow.querySelector("[slot='push-body-sidenav']");

    // Initialize smart views as a class instead of putting it
    // directly into the template.ts HTML because it's constructor
    // doesn't get called otherwise because of unknown reasons
    // (this bug only occurs in console, not in the storybook)
    this.dom.$smartViews = new GridSmartViewsWebComponent();
    this.dom.$sideBarContent.appendChild(this.dom.$smartViews);

    this.dom.$gridTitle = this.shadow.querySelector('#grid-title');
    this.dom.$grid = this.shadow.querySelector('gz-grid-view');

    this.dom.$executeTooltip = this.shadow.querySelector('#executeTooltip');
    this.dom.$executeButton = this.shadow.querySelector('#executeButton');
    this.dom.$manageTextButton = this.shadow.querySelector('#manageButtonText');
    this.dom.$manageButtonSelections = this.shadow.querySelector('#manageButtonSelections');

    // getDomElements is called at start, and also when the component finished initializing all its children.
    // It's only in this second step that we have the grid initialized.
    if (this.dom.$grid.injectDependencies) {
      this.dom.$grid.injectDependencies(this.dependencies);
      this.dom.$grid.start().then().catch(console.error);
    }

    if (this.dom.$smartViews.injectDependencies) {
      this.dom.$smartViews.injectDependencies(this.dependencies);
      this.dom.$smartViews.start().then().catch(console.error);
    }
  }

  public async buildSuccessView(viewSettings: ResponseGridWebComponentSchema) {
    this.viewSettings = viewSettings;

    // Set isOpen to true to open the side panel by default
    this.dom.$layout.setAttribute('isOpen', '');
    this.dom.$grid.titleConfig = new GridTitleConfig('Placeholder title');

    // Set the title of the side panel, not of the smart view to conform to the mockup
    this.dom.$smartViewsTitle.innerText = this.dependencies.translateFn('VIEWS');

    if (this.dom.$smartViews.injectDependencies) {
      this.buildSmartViews(viewSettings);
    }

    this.buildGridView(viewSettings);
  }

  private buildSmartViews(viewSettings: ResponseGridWebComponentSchema) {
    this.dom.$smartViews.propertiesSet = false;
    this.dom.$smartViews.panelTitle = '';

    this.dom.$smartViews.activeViewId = viewSettings.defaultViewProps.id;

    this.dom.$smartViews.sections = [this.getDefaultViewSectionFromViewSettings(viewSettings)];
    this.dom.$smartViews.propertiesSet = true;
    this.dom.$smartViews.injectDependencies(this.dependencies);
    this.dom.$smartViews.start().catch(console.error);
  }

  private buildGridView(viewSettings: ResponseGridWebComponentSchema) {
    const defaultView = viewSettings.defaultViewProps;

    this.dom.$grid.propertiesSet = false;

    this.gridDataProvider = new ResponseGridDataProvider(this.dependencies);
    this.dom.$grid.dataProvider = this.gridDataProvider;
    this.gridDataProvider.setColumns(defaultView.displayedColumns);
    this.gridDataProvider.setFiltersSync({
      filters: defaultView.filters,
      viewId: defaultView.id,
    });

    this.dom.$grid.predefinedFiltersKeys = viewSettings.predefinedFilterKeys;
    this.dom.$grid.allColumnDetails = viewSettings.allColumns;
    this.dom.$grid.isCompactViewChecked = false;
    this.dom.$grid.defaultViewProps = defaultView;
    this.setViewProps(defaultView);
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.USER_INLINE_ACTIONS]: this.userInlineActionsRenderer.bind(this),
      [ColumnKeys.EXECUTED_ON]: this.executedOnRenderer,
      [ColumnKeys.DETAILS]: this.detailsRenderer.bind(this),
      [ColumnKeys.STATUS]: this.statusRenderer.bind(this),
    };

    this.addTextToButtons();

    this.dom.$grid.propertiesSet = true;
  }

  public setViewProps(viewConfig: ViewConfiguration<Column, ColumnKeys>) {
    this.dom.$grid.titleConfig = new GridTitleConfig(viewConfig.name);
    this.dom.$grid.viewId = viewConfig.id;
    this.dom.$grid.columns = viewConfig.displayedColumns;
    this.dom.$grid.filters = viewConfig.filters;
    this.dom.$grid.pageSize = viewConfig.pageSize;
  }

  private statusRenderer(rowData) {
    const statusMsg = this.stringsTranslator.translateXdrResponseStatus(rowData['item']['status']);
    if (rowData['item']['status'] === Status.EXECUTION_FAILED) {
      const errorDescription = this.stringsTranslator.translateXdrResponseActionsError(rowData['item']['errorId'], rowData['item']['errorDescription']);
      return html`
        <gz-tooltip tooltiptext="${errorDescription}" tooltipclass="neutral" part="final-details">
          <div>${statusMsg}</div>
        </gz-tooltip>`;
    }

    return statusMsg;
  }

  private detailsRenderer(rowData) {
    const responseConstants: ResponseConstants = this.dependencies.responseConstants;

    try {
      const dictionary = this.dependencies.dictionary;
      const finalDetails: any[] = [];
      const details = rowData['item']['details'];

      if (!details || !details.hasOwnProperty('translationMatrix')) {
        return ['-'];
      }

      details['translationMatrix'].forEach(translation => {
        let phrase = '';
        const phraseId = translation['id'];
        const dictionaryChapter = 'xdr_overview_event_recommendation.phrases';
        const dictionaryPath = dictionaryChapter + '.' + phraseId;
        const matrixDictionaryTemplate = getPropertyByPath(dictionary, dictionaryPath, '');

        const enumerationsParamsToReplace = getParams(
          translation['enumerations'],
          details['phrases'],
          details['version'],
          responseConstants.ENUMERATION,
          'recommendation',
          dictionary,
          'xdr_overview_event_',
          this.processParamsToReplace.bind(this)
        );

        if (!enumerationsParamsToReplace) {
          return;
        }

        phrase += replaceParams(matrixDictionaryTemplate, enumerationsParamsToReplace);
        phrase += ' ';

        finalDetails.push(phrase);
      });

      return finalDetails.length > 0
        ? html` <gz-tooltip tooltiptext="${finalDetails}" tooltipclass="neutral" part="final-details">
            <div>${finalDetails}</div>
          </gz-tooltip>`
        : ['-'];
    } catch (e) {
      return ['-'];
    }
  }

  /**
   * returns the number of elements if there is > 1, otherwise value of it
   *
   * @param summaryParams
   * @param notReplacedDictionaryTemplate
   * @returns {{}}
   */
  processParamsToReplace(summaryParams: Array<any>, notReplacedDictionaryTemplate: string): any {
    const paramsToReplace = {};

    summaryParams.forEach((param, paramIndex) => {
      if (param['value'].length > 1) {
        paramsToReplace[paramIndex] = param['value'].length;
      } else {
        // UNKNOWN - just display it
        paramsToReplace[paramIndex] = param['value'][0];
      }
    });

    return replaceParams(notReplacedDictionaryTemplate, paramsToReplace);
  }

  private executedOnRenderer(rowData): string {
    const { executedOn } = rowData.item;

    if (!executedOn || '-' === executedOn) {
      return '-';
    }

    return dateFormatter(executedOn, '');
  }

  private userInlineActionsRenderer(rowData): any {
    if (!Application.Main.hasAnyRights([Rights.RIGHT_MANAGE_INVENTORY])) {
      return null;
    }

    const actions = {
      [InlineActionStatus.PENDING]: {
        title: this.dependencies.translateFn('Execute'),
        value: Status.PENDING.toString(),
      },
      [InlineActionStatus.PENDING_FOR_RETRY]: {
        title: this.dependencies.translateFn('Retry'),
        value: Status.PENDING.toString(),
      },
      [InlineActionStatus.DONE_EXTERNALLY]: {
        title: this.dependencies.translateFn('Mark as done'),
        value: Status.DONE_EXTERNALLY.toString(),
      },
      [InlineActionStatus.DISMISSED]: {
        title: this.dependencies.translateFn('Dismiss'),
        value: Status.DISMISSED.toString(),
      },
      [InlineActionStatus.TO_BE_EXECUTED_FROM_PENDING]: {
        title: this.dependencies.translateFn('Cancel'),
        value: Status.TO_BE_EXECUTED.toString(),
      },
      [InlineActionStatus.TO_BE_EXECUTED_FROM_DISMISSED_AND_EXECUTED]: {
        title: this.dependencies.translateFn('Restore'),
        value: Status.TO_BE_EXECUTED.toString(),
      },
      [InlineActionStatus.TO_BE_EXECUTED]: {
        title: this.dependencies.translateFn('Action needed'),
        value: Status.TO_BE_EXECUTED.toString(),
      },
      [InlineActionStatus.EXTERNAL_ACTION_NEEDED]: {
        title: this.dependencies.translateFn('External action needed'),
        value: Status.EXTERNAL_ACTION_NEEDED.toString(),
      },
      [InlineActionStatus.EXTERNAL_ACTION_NEEDED_FROM_DISMISSED_AND_EXECUTED]: {
        title: this.dependencies.translateFn('Restore'),
        value: Status.EXTERNAL_ACTION_NEEDED.toString(),
      },
    };

    const optionsBasedOnExecutableType: any[] = [];
    const typeOption = ResponseConstants.EXECUTABLE_ACTION_TYPES.includes(rowData['item']['type'])
      ? actions[InlineActionStatus.TO_BE_EXECUTED_FROM_DISMISSED_AND_EXECUTED]
      : actions[InlineActionStatus.EXTERNAL_ACTION_NEEDED_FROM_DISMISSED_AND_EXECUTED];
    optionsBasedOnExecutableType.unshift(typeOption);

    const actionsMap = {
      [Status.TO_BE_EXECUTED]: [actions[Status.PENDING], actions[Status.DONE_EXTERNALLY], actions[Status.DISMISSED]],
      //[ResponseConstants.STATUS_PENDING]: [actions[Status.TO_BE_EXECUTED_FROM_PENDING]],
      [Status.EXECUTION_FAILED]: [
        actions[InlineActionStatus.PENDING_FOR_RETRY],
        actions[Status.DONE_EXTERNALLY],
        actions[Status.DISMISSED],
      ],
      [Status.DONE_EXTERNALLY]: optionsBasedOnExecutableType,
      [Status.DISMISSED]: optionsBasedOnExecutableType,
      [Status.EXTERNAL_ACTION_NEEDED]: [actions[Status.DONE_EXTERNALLY], actions[Status.DISMISSED]],
    };
    const select = document.createElement('gz-select') as GzSelectWebComponent;

    select.options = actionsMap[rowData['item']['status']];
    select.classList.add('d-flex', 'align-items-center', 'h-full');
    select.innerHTML =
      `<gz-icon-button icon="ds-icon-more" buttoncolor="secondary" buttontype="square" ` +
      `fontsize="22" size="24" buttonfill="quiet" class="inline-menu-trigger"></gz-icon-button`;

    select.addEventListener(SelectEvents.SELECTION_CHANGED, async (e: WebComponentEvent) => {
      await this.onActionSelectionChange(e, rowData['item']['_id'], rowData['item']['status']);
    });

    if (this.dependencies.isGzDemo) {
      select.options.forEach(option => (option.disabled = true));
    }

    return select.options !== undefined ? select : null;
  }

  private async onActionSelectionChange(e: WebComponentEvent, actionId: any, oldStatus: number) {
    const responseConstants: ResponseConstants = this.dependencies.responseConstants;

    try {
      const response = await this.dependencies.api.clients.GridItemsProvider.setRecordsStatus({
        actionIds: [actionId],
        newStatus: e.detail['selected'] !== undefined ? e.detail['selected'] : Status.PENDING,
      });

      if (response['success']) {
        const messageBuilder = responseConstants.TOAST_MESSAGES_SUCCESSFUL[parseInt(e['detail']['selected'], 10)];
        const clientMessage = oldStatus === Status.PENDING ? messageBuilder[2] : messageBuilder[0];
        this.showToast(ToastSeverity.SUCCESS, clientMessage);
      }
    } catch (error) {
      const isExecuteOrRetry = parseInt(e['detail']['selected'], 10) === Status.PENDING;

      this.showToast(
        ToastSeverity.ERROR,
        getChangeStatusFailureMessage(isExecuteOrRetry, error['code'], 1, responseConstants)
      );
    } finally {
      await this.dom.$grid.controller.handleGridItemsLoad();
    }
  }

  private showToast(severity: any, message: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message,
      severity: severity,
    });
  }

  private addManageButtonSelectionsOptions(): void {
    const responseConstants: ResponseConstants = this.dependencies.responseConstants;

    this.dom.$manageButtonSelections.options = Object.values(responseConstants.MANAGE_ACTIONS).map(action => {
      return {
        title: action.title,
        value: action.value,
      };
    });
  }

  private addTextToButtons(): void {
    this.dom.$executeButton.innerText = this.dependencies.translateFn('EXECUTE');
    this.dom.$manageTextButton.innerText = this.dependencies.translateFn('MANAGE');
    this.addManageButtonSelectionsOptions();
  }

  private getDefaultViewSectionFromViewSettings(
    viewSettings: ResponseGridWebComponentSchema
  ): SmartViewSection<Column, Filter<ColumnKeys>> {
    const defaultViewConfigs = viewSettings.views.filter(view => view.type === ViewType.DEFAULT);

    return {
      type: SectionType.DEFAULT,
      sectionTitle: this.dependencies.translateFn('DEFAULTS'),
      inlineActions: [],
      items: defaultViewConfigs.map(convertViewConfigToSmartConfig),
      smartViewValidators: [],
    };
  }
}
